import type { Workspace } from './types';

import { gql } from '@apollo/client';
import { createAsyncThunk } from '@reduxjs/toolkit';

import { apolloClient } from 'src/lib/apollo-client';

import * as ActionTypes from './action-types';

// Re-export action type constants
export const WORKSPACE_WORKSPACES_LOADED = ActionTypes.WORKSPACE_WORKSPACES_LOADED;
export const WORKSPACE_WORKSPACE_SELECTED = ActionTypes.WORKSPACE_WORKSPACE_SELECTED;
export const WORKSPACE_WORKSPACE_CREATED = ActionTypes.WORKSPACE_WORKSPACE_CREATED;
export const WORKSPACE_WORKSPACE_UPDATED = ActionTypes.WORKSPACE_WORKSPACE_UPDATED;
export const WORKSPACE_LOADING_CHANGED = ActionTypes.WORKSPACE_LOADING_CHANGED;

// Re-export action types
export type WorkspacesLoadedAction = ActionTypes.IWorkspacesLoadedAction;
export type WorkspaceSelectedAction = ActionTypes.IWorkspaceSelectedAction;
export type WorkspaceCreatedAction = ActionTypes.IWorkspaceCreatedAction;
export type WorkspaceUpdatedAction = ActionTypes.IWorkspaceUpdatedAction;
export type WorkspaceLoadingChangedAction = ActionTypes.IWorkspaceLoadingChangedAction;

export type WorkspaceActionTypes = ActionTypes.WorkspaceActionTypes;

// Event-based action creators
export const workspacesLoaded = (workspaces: Workspace[]) =>
  ({
    type: WORKSPACE_WORKSPACES_LOADED,
    payload: { workspaces },
  }) as const;

export const workspaceSelected = (workspaceId: string) =>
  ({
    type: WORKSPACE_WORKSPACE_SELECTED,
    payload: { workspaceId },
  }) as const;

export const workspaceCreated = (workspace: Workspace) =>
  ({
    type: WORKSPACE_WORKSPACE_CREATED,
    payload: { workspace },
  }) as const;

export const workspaceUpdated = (workspace: Workspace) =>
  ({
    type: WORKSPACE_WORKSPACE_UPDATED,
    payload: { workspace },
  }) as const;

export const workspaceLoadingChanged = (isLoading: boolean) =>
  ({
    type: WORKSPACE_LOADING_CHANGED,
    payload: { isLoading },
  }) as const;

// Type for GraphQL query response
interface GetUserWorkspacesData {
  me: {
    id: string;
    workspaces: {
      edges: Array<{
        role: string;
        node: {
          id: string;
          name: string;
        };
      }>;
    };
  };
}

// Async thunk for loading workspaces from GraphQL
export const loadWorkspaces = createAsyncThunk('workspace/loadWorkspaces', async () => {
  const { data } = await apolloClient.query<GetUserWorkspacesData>({
    query: gql`
      query GetUserWorkspaces {
        me {
          id
          workspaces {
            edges {
              role
              node {
                id
                name
              }
            }
          }
        }
      }
    `,
    fetchPolicy: 'network-only',
  });

  if (!data) {
    throw new Error('Failed to load workspaces: No data returned');
  }

  const workspaces = data.me.workspaces.edges.map((edge) => ({
    id: edge.node.id,
    name: edge.node.name,
    role: edge.role,
    logo: '/assets/icons/workspaces/logo-1.webp',
  }));

  return workspaces as Workspace[];
});
